// WMO 4677 weather codes → emoji + 繁體中文 (W47.1).
//
// This mirrors `backend/app/modules/weather/service/wmo.py` one-for-one, and the
// mirror is deliberate rather than convenient: the **backend** owns the mapping
// (it is the only place that talks to the upstream, and `WeatherSummary` already
// carries `emoji` + `label` in the payload). This file exists for one case —
// a card rendered from **cached** data whose `emoji` predates a table change —
// and for the tests, which need to resolve a code without a network round trip.
//
// The duplication is therefore narrow and the invariant is mechanical:
// `wmo-parity.test.ts` asserts that every group this file declares has the same
// bounds and the same two strings as the Python table, read out of the source
// rather than transcribed into a third copy of the numbers.

/** One resolved condition: what to draw, and what to write. */
export interface WeatherCondition {
  emoji: string;
  label: string;
}

export const UNKNOWN_CONDITION: WeatherCondition = {
  emoji: "🌡️",
  label: "天氣情況未知",
};

/**
 * Group bounds, exactly as `wmo.py` declares them.
 *
 * Ranges rather than individual codes: WMO publishes group codes (3xx drizzle,
 * 5xx rain, 7xx snow, 9xx thunderstorm) and each group has levels. A lookup
 * written as `LABELS[code]` silently renders nothing for every code it forgot —
 * and for a forecast, unlisted codes are the common case, not the rarest.
 */
export const WMO_GROUPS: readonly (readonly [number, number, string, string])[] = [
  [0, 0, "☀️", "晴"],
  [1, 1, "🌤️", "大致晴朗"],
  [2, 2, "⛅", "局部多雲"],
  [3, 3, "☁️", "陰"],
  [45, 48, "🌫️", "霧"],
  [51, 57, "🌦️", "毛毛雨"],
  [61, 67, "🌧️", "有雨"],
  [71, 77, "❄️", "有雪"],
  [80, 82, "🌦️", "陣雨"],
  [85, 86, "❄️", "陣雪"],
  [95, 99, "⛈️", "雷暴"],
];

/** Resolve a WMO code. Never throws; an unknown code resolves to a real label. */
export function conditionFor(code: number | null | undefined): WeatherCondition {
  if (code === null || code === undefined) return UNKNOWN_CONDITION;
  for (const [lo, hi, emoji, label] of WMO_GROUPS) {
    if (code >= lo && code <= hi) return { emoji, label };
  }
  return UNKNOWN_CONDITION;
}

/**
 * One decimal, always.
 *
 * The upstream returns JSON numbers, so `24.0` and `29.0` arrive as `24` and
 * `29`. Interpolating them directly printed 「24°」 where the source said
 * 「24.0°」 — two of six temperatures drifted that way in the first mockup before
 * the assertion against the captured response caught it. Weather without the
 * decimal reads as a different number, not as the same number written shorter.
 */
export function temp(value: number | null | undefined): string {
  return value === null || value === undefined ? "—" : `${value.toFixed(1)}°`;
}

/** Whole percent, or a dash when the upstream had no probability. */
export function pct(value: number | null | undefined): string {
  return value === null || value === undefined ? "—" : `${value}%`;
}

/** `2026-10-09` → `週六`. The card labels days in the user's own words. */
export function weekdayZh(isoDate: string): string {
  const parsed = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return isoDate;
  const days = ["週日", "週一", "週二", "週三", "週四", "週五", "週六"];
  // `noUncheckedIndexedAccess` makes the lookup `string | undefined` even though
  // `getDay()` is 0–6, so the fallback is unreachable rather than absent.
  return days[parsed.getDay()] ?? isoDate;
}